/**
 * Master Visual Representation Registry for "The Caesura Engine"
 *
 * Catalogs 58 distinct, micro-animated visual scenes and repeatable character representations
 * across 7 major thematic pillars:
 *
 * 1. Characters & Psychological States (12 representations)
 * 2. Vast Cosmic Spaces & Voids (10 representations)
 * 3. Atmospheric Megastructures & Stations (8 representations)
 * 4. Hard-Surface Fleets & Industrial Keels (8 representations)
 * 5. Grimy Interiors & Cryo Wells (8 representations)
 * 6. Architecture, Transit & Tribunals (6 representations)
 * 7. Existential Horror & Cosmic Ledger (6 representations)
 */

import { ArtworkStyle } from '../types/tome';
import { CharacterId, CharacterState } from '../types/character';
import { generateCharacterPortrait } from './characterDesigns';
import { generateProceduralSvg } from './proceduralArt';
import { GENERATIVE_ART_COLLECTION } from '../data/generativeArtManifest';

export interface VisualEntry {
  id: string;
  title: string;
  category:
    | 'Characters & Crew'
    | 'Cosmic Voids & Spaces'
    | 'Megastructures & Stations'
    | 'Fleets & Ships'
    | 'Interiors & Wells'
    | 'Architecture & Transit'
    | 'Existential Horror & Ledger'
    | 'Generative Concept Art (150)';
  characterId?: CharacterId;
  characterState?: CharacterState;
  style: ArtworkStyle;
  accentColor: string;
  secondaryColor: string;
  heroImage?: string;
  prompt: string;
  caption: string;
  svgDataUri: string;
}

export const GENERATIVE_VISUAL_ENTRIES: VisualEntry[] = GENERATIVE_ART_COLLECTION.map((g) => ({
  id: g.id,
  title: `${g.title} (Plate #${g.plate_number})`,
  category: 'Generative Concept Art (150)' as const,
  style: 'quantum_schematic' as const,
  accentColor: '#00f3ff',
  secondaryColor: '#f59e0b',
  heroImage: g.relative_path,
  prompt: `${g.title}. ${g.description}`,
  caption: `USSC GERYON // ${g.cycle_title.toUpperCase()} // ${g.act.toUpperCase()} // 16:9 1280x720`,
  svgDataUri: '',
}));

export const VISUAL_REPRESENTATIONS: VisualEntry[] = [
  // =========================================================
  // 1. CHARACTERS & CREW (12 Repeatable Micro-Animated Portraits)
  // =========================================================
  {
    id: 'char_marcus_neutral',
    title: 'Marcus Ward — Pre-Flight Calibration',
    category: 'Characters & Crew',
    characterId: 'marcus_ward',
    characterState: 'neutral',
    style: 'quantum_schematic',
    accentColor: '#00f3ff',
    secondaryColor: '#4d555c',
    heroImage: '/art/marcus_ward_portrait.jpg',
    prompt: 'Cinematic portrait of Marcus Ward, 54-year-old jump engineer, graying stubble, neuro-lattice collar, Thorne-Vance jumpsuit, 8k.',
    caption: 'ID #0x992B-WARD // NEURO-LATTICE IDLE // CALIBRATION 40.0Hz',
    svgDataUri: generateCharacterPortrait('marcus_ward', 'neutral', { subtitle: 'DIAGNOSTIC STATUS: NOMINAL' }),
  },
  {
    id: 'char_marcus_weary',
    title: 'Marcus Ward — Cognitive Exhaustion',
    category: 'Characters & Crew',
    characterId: 'marcus_ward',
    characterState: 'weary',
    style: 'phosphor_spine',
    accentColor: '#00f3ff',
    secondaryColor: '#ffb700',
    prompt: 'Exhausted portrait of Marcus Ward, hollow cheeks, dark eye bags, grease-stained jumpsuit, flickering emergency light, 8k.',
    caption: 'WARD, M. // 24-HOUR DOUBLE SHIFT // COGNITIVE FATIGUE: HIGH',
    svgDataUri: generateCharacterPortrait('marcus_ward', 'weary', { subtitle: 'CHRONO-FATIGUE FLAG DS-14' }),
  },
  {
    id: 'char_marcus_synaptic_bleed',
    title: 'Marcus Ward — Retinal Grid Bleed',
    category: 'Characters & Crew',
    characterId: 'marcus_ward',
    characterState: 'synaptic_bleed',
    style: 'synaptic_tear',
    accentColor: '#ff2a4b',
    secondaryColor: '#00f3ff',
    prompt: 'Intense psychological sci-fi portrait of Marcus Ward suffering retinal grid bleed, glowing crimson eye micro-saccades, neuro-collar glitching, 8k.',
    caption: 'WARD, M. // 40Hz MICRO-SACCADE OSCILLATION // OPTIC FIBROSIS ACTIVE',
    svgDataUri: generateCharacterPortrait('marcus_ward', 'synaptic_bleed', { subtitle: 'STAGE-II CHRONOGNOSTIC APHASIA' }),
  },
  {
    id: 'char_marcus_stasis_frost',
    title: 'Marcus Ward — Post-Stasis Emergence',
    category: 'Characters & Crew',
    characterId: 'marcus_ward',
    characterState: 'stasis_frost',
    style: 'cryostat_choke',
    accentColor: '#00e5ff',
    secondaryColor: '#ffffff',
    prompt: 'Marcus Ward emerging from stasis pod, crystalline frost across gray hair and jumpsuit collar, pale shivering skin, cold vapor, 8k.',
    caption: 'WARD, M. // CORE TEMPERATURE 34.2C // CRYO-FLUSH DE-SATURATION',
    svgDataUri: generateCharacterPortrait('marcus_ward', 'stasis_frost', { subtitle: 'HYPOTHERMIC RE-SYNCHRONIZATION' }),
  },
  {
    id: 'char_elena_neutral',
    title: 'Elena Cruz — Chief Architect',
    category: 'Characters & Crew',
    characterId: 'elena_cruz',
    characterState: 'neutral',
    style: 'quantum_schematic',
    accentColor: '#e040fb',
    secondaryColor: '#00f3ff',
    heroImage: '/art/elena_cruz_portrait.jpg',
    prompt: 'Cinematic portrait of Chief Architect Elena Cruz, severe cropped black hair, cyan ocular monocle over right eye, navy uniform, 8k.',
    caption: 'CRUZ, E. // ARCHITECT #01 // OCULAR RETICLE: COORDINATE LOCK',
    svgDataUri: generateCharacterPortrait('elena_cruz', 'neutral', { subtitle: 'NAVIGATION GUILD COMMAND' }),
  },
  {
    id: 'char_elena_synaptic',
    title: 'Elena Cruz — Coordinate Obsession',
    category: 'Characters & Crew',
    characterId: 'elena_cruz',
    characterState: 'synaptic_bleed',
    style: 'event_horizon',
    accentColor: '#ff1744',
    secondaryColor: '#e040fb',
    prompt: 'Elena Cruz staring fiercely through glitched ocular monocle displaying red warning formulas, strained jaw, intense existential focus, 8k.',
    caption: 'CRUZ, E. // RECURSIVE SYNTAX INTEGRAL // OCULAR STRAIN 94%',
    svgDataUri: generateCharacterPortrait('elena_cruz', 'synaptic_bleed', { subtitle: 'COORDINATE MATRIX OVERRIDE' }),
  },
  {
    id: 'char_elena_interrogation',
    title: 'Elena Cruz — Corporate Defiance',
    category: 'Characters & Crew',
    characterId: 'elena_cruz',
    characterState: 'interrogation',
    style: 'corporate_tribunal',
    accentColor: '#ffffff',
    secondaryColor: '#00f3ff',
    prompt: 'Elena Cruz under harsh tribunal interrogation spotlight, silent contempt, monochrome uniform, 8k.',
    caption: 'CRUZ, E. // TESTIMONY REFUSED // EXECUTIVE WAIVER INVOKED',
    svgDataUri: generateCharacterPortrait('elena_cruz', 'interrogation', { subtitle: 'TRIBUNAL DOCKET #TV-992' }),
  },
  {
    id: 'char_simon_neutral',
    title: 'Dr. Simon Peter — Chief Medical Officer',
    category: 'Characters & Crew',
    characterId: 'dr_simon_peter',
    characterState: 'neutral',
    style: 'chrono_psych_ward',
    accentColor: '#ffb700',
    secondaryColor: '#00e5ff',
    heroImage: '/art/dr_simon_peter_portrait.jpg',
    prompt: 'Dr. Simon Peter, 61, wireframe amber spectacles, creased compassionate brow, thermal medical smock, holding Chronozepine injector, 8k.',
    caption: 'PETER, S. (MD) // CHIEF MEDICAL // BIOMETRIC SCANNER: ACTIVE',
    svgDataUri: generateCharacterPortrait('dr_simon_peter', 'neutral', { subtitle: 'DSM-CHRONO EVALUATION BOARD' }),
  },
  {
    id: 'char_simon_weary',
    title: 'Dr. Simon Peter — Clinical Despair',
    category: 'Characters & Crew',
    characterId: 'dr_simon_peter',
    characterState: 'weary',
    style: 'synaptic_decay',
    accentColor: '#ffb700',
    secondaryColor: '#ff1744',
    prompt: 'Exhausted medical officer Dr. Peter removing spectacles, red rimmed eyes, darkened ward background, existential sorrow, 8k.',
    caption: 'PETER, S. // DSM-CHRONO CRITERIA EXCEEDED // CHRONOZEPINE DEPLETED',
    svgDataUri: generateCharacterPortrait('dr_simon_peter', 'weary', { subtitle: 'PSYCHIATRIC ADVISORY SEALED' }),
  },
  {
    id: 'char_silas_panic',
    title: 'Cadet Silas Vance — The First Jump',
    category: 'Characters & Crew',
    characterId: 'cadet_silas',
    characterState: 'synaptic_bleed',
    style: 'unrendered_void',
    accentColor: '#ffea00',
    secondaryColor: '#ff5252',
    heroImage: '/art/silas_vance_portrait.jpg',
    prompt: 'Youthful rookie technician Cadet Silas Vance, wide panicked eyes reflecting warning beacons, yellow combine epaulets, trembling, 8k.',
    caption: 'VANCE, S. // CADET #402 // ACUTE CHRONO-PANIC // FIRST TRANSIT',
    svgDataUri: generateCharacterPortrait('cadet_silas', 'synaptic_bleed', { subtitle: 'COMBINE APPRENTICESHIP REGISTER' }),
  },
  {
    id: 'char_inquisitor_thorne',
    title: 'Inquisitor Vane Thorne — Directorate Tribunal',
    category: 'Characters & Crew',
    characterId: 'inquisitor_thorne',
    characterState: 'interrogation',
    style: 'corporate_tribunal',
    accentColor: '#ff1744',
    secondaryColor: '#212121',
    heroImage: '/art/inquisitor_thorne_portrait.jpg',
    prompt: 'Predatory corporate inquisitor Vane Thorne, slicked black hair, cold ruthless eyes, black and crimson high-collar trench coat, 8k.',
    caption: 'THORNE, V. // HIGH INQUISITOR // SEIZURE WARRANT #882-PERSEUS',
    svgDataUri: generateCharacterPortrait('inquisitor_thorne', 'interrogation', { subtitle: 'EXECUTIVE LEGAL DIRECTORATE' }),
  },
  {
    id: 'char_margin_spectator',
    title: 'The Spectator in the Margin',
    category: 'Characters & Crew',
    characterId: 'margin_spectator',
    characterState: 'neutral',
    style: 'spectators_margin',
    accentColor: '#aa00ff',
    secondaryColor: '#ffffff',
    heroImage: '/art/spectator_margin_entity.jpg',
    prompt: 'Eerie non-Euclidean geometric silhouette standing in timeless Planck interval, glowing white slit eyes, chilling cosmic dread, 8k.',
    caption: 'ENTITY // NON-EUCLIDEAN COORDINATE // BREATHING: NONE // WATCHING: ETERNAL',
    svgDataUri: generateCharacterPortrait('margin_spectator', 'neutral', { subtitle: 'INTERVAL RESIDENT // TIME: 0.00s' }),
  },

  // =========================================================
  // 2. VAST COSMIC SPACES & VOIDS (10 Visual Representations)
  // =========================================================
  {
    id: 'cosmic_planck_ignition',
    title: 'Planck Ignition Solar Flare (Hero 1)',
    category: 'Cosmic Voids & Spaces',
    style: 'cosmic_flare',
    accentColor: '#e040fb',
    secondaryColor: '#00f3ff',
    heroImage: '/art/chapter1_tick_and_void.jpg',
    prompt: 'Vast cosmic planetary orbital gantry, blinding Planck ignition solar flash across twin moons and purple nebula, intricate station spires, 8k dark sci-fi masterpiece.',
    caption: 'ORBITAL GANTRY // PLANCK IGNITION BURST // MAGENTA DAWN',
    svgDataUri: generateProceduralSvg({ pageNumber: 1, style: 'cosmic_flare', title: 'Planck Ignition Solar Flare' }),
  },
  {
    id: 'cosmic_event_horizon_rift',
    title: 'The Great Null Rift Singularity',
    category: 'Cosmic Voids & Spaces',
    style: 'event_horizon',
    accentColor: '#7c4dff',
    secondaryColor: '#ff2a4b',
    heroImage: '/art/chapter5_null_rift.jpg',
    prompt: 'Massive gravitational singularity tearing spacetime fabric, relativistic warped accretion disk, Doppler blue-shift, pitch black void core, 8k.',
    caption: 'SINGULARITY EVENT HORIZON // NULL RIFT VECTOR // -0.44c WARP',
    svgDataUri: generateProceduralSvg({ pageNumber: 101, style: 'event_horizon', title: 'Great Null Rift Singularity' }),
  },
  {
    id: 'cosmic_stellar_nursery_dust',
    title: 'Turquoise Stellar Nursery Filaments',
    category: 'Cosmic Voids & Spaces',
    style: 'stellar_nursery',
    accentColor: '#00e5ff',
    secondaryColor: '#76ff03',
    prompt: 'Breathtaking ionized gas filaments of turquoise and emerald stellar nursery, cosmic dust clouds backlighting solitary starship keel, 8k.',
    caption: 'IONIZED GAS FILAMENTS // TURQUOISE NEBULAR VEIL // ION DRIFT',
    svgDataUri: generateProceduralSvg({ pageNumber: 76, style: 'stellar_nursery', title: 'Turquoise Stellar Nursery' }),
  },
  {
    id: 'cosmic_unrendered_gap_white',
    title: 'The Unrendered Gap Matrix',
    category: 'Cosmic Voids & Spaces',
    style: 'unrendered_void',
    accentColor: '#00f3ff',
    secondaryColor: '#ffffff',
    heroImage: '/art/chapter11_stillness_breathes.jpg',
    prompt: 'Infinitesimal pause between two Planck clock-ticks, pure white mathematical coordinate lattice stretching across infinite stillness, 8k.',
    caption: 'CHRONO INTERVAL 5.39E-44s // ABSOLUTE REST // UNRENDERED STILLNESS',
    svgDataUri: generateProceduralSvg({ pageNumber: 251, style: 'unrendered_void', title: 'The Unrendered Gap Matrix' }),
  },
  {
    id: 'cosmic_perseus_rim_twilight',
    title: 'Perseus Rim Emergence Twilight',
    category: 'Cosmic Voids & Spaces',
    style: 'vast_spaces',
    accentColor: '#536dfe',
    secondaryColor: '#00f3ff',
    prompt: 'Emergence at distant Perseus Rim, unfamiliar constellations, cold blue starlight reflecting on scarred titanium armor, vast vacuum expanse, 8k.',
    caption: 'PERSEUS RIM ARRIVAL // COORDINATE SHIFT: +42.1 LY // LIGHT ARRIVAL',
    svgDataUri: generateProceduralSvg({ pageNumber: 801, style: 'vast_spaces', title: 'Perseus Rim Emergence' }),
  },
  {
    id: 'cosmic_doppler_blueshift_arc',
    title: 'Doppler Blue-Shift Accretion Arc',
    category: 'Cosmic Voids & Spaces',
    style: 'event_horizon',
    accentColor: '#00b0ff',
    secondaryColor: '#e040fb',
    prompt: 'Relativistic Doppler blue-shift arc warping starfield light into brilliant concentric rings as ship enters unthreaded space, 8k.',
    caption: 'RELATIVISTIC ARC // DOPPLER COMPRESSION: 12.8x // FIELD WARP',
    svgDataUri: generateProceduralSvg({ pageNumber: 126, style: 'event_horizon', title: 'Doppler Blue-Shift Arc' }),
  },
  {
    id: 'cosmic_non_euclidean_matrix',
    title: 'Non-Euclidean Coordinate Chasm',
    category: 'Cosmic Voids & Spaces',
    style: 'unrendered_void',
    accentColor: '#9c27b0',
    secondaryColor: '#00f3ff',
    prompt: 'Chasm of twisted coordinate space where Euclidean geometry breaks, floating fractured perspective grids, cold cosmic dread, 8k.',
    caption: 'NON-EUCLIDEAN CHASM // METRIC TENSOR BREAKDOWN // R_NULL',
    svgDataUri: generateProceduralSvg({ pageNumber: 326, style: 'unrendered_void', title: 'Non-Euclidean Coordinate Chasm' }),
  },
  {
    id: 'cosmic_twin_moons_eclipse',
    title: 'Twin Moons Occultation',
    category: 'Cosmic Voids & Spaces',
    style: 'cosmic_flare',
    accentColor: '#e040fb',
    secondaryColor: '#ffd600',
    prompt: 'Colossal gas giant with twin crescent moons in cosmic alignment, intense corona flare, orbital debris field, 8k masterpiece.',
    caption: 'OCCULTATION PHENOMENON // TWIN SATELLITES // CORONAL BLEED',
    svgDataUri: generateProceduralSvg({ pageNumber: 55, style: 'cosmic_flare', title: 'Twin Moons Occultation' }),
  },
  {
    id: 'cosmic_deep_vacuum_darkness',
    title: 'Absolute Deep Vacuum Horizon',
    category: 'Cosmic Voids & Spaces',
    style: 'cosmic_void',
    accentColor: '#00f3ff',
    secondaryColor: '#37474f',
    prompt: 'Infinite black vacuum devoid of starlight, cold lonely interstellar gulf, tiny glowing ship antenna beacon, 8k.',
    caption: 'DEEP VACUUM VOID // ZERO PHOTON FLUX // SILENT GULF',
    svgDataUri: generateProceduralSvg({ pageNumber: 276, style: 'cosmic_void', title: 'Deep Vacuum Horizon' }),
  },
  {
    id: 'cosmic_frozen_photons_amber',
    title: 'Frozen Photons in the Seam',
    category: 'Cosmic Voids & Spaces',
    style: 'unrendered_void',
    accentColor: '#ffd600',
    secondaryColor: '#00f3ff',
    prompt: 'Light trapped mid-propagation like golden insect in amber within the zero-time Planck seam, static light vectors, 8k.',
    caption: 'ZERO PROPAGATION VELOCITY // PHOTONS IN AMBER // TIME HALTED',
    svgDataUri: generateProceduralSvg({ pageNumber: 280, style: 'unrendered_void', title: 'Frozen Photons in Seam' }),
  },

  // =========================================================
  // 3. ATMOSPHERIC MEGASTRUCTURES & STATIONS (8 Representations)
  // =========================================================
  {
    id: 'station_cloud_aerostat_golden',
    title: 'Jovian Cloud Aerostat Domes (Hero 2)',
    category: 'Megastructures & Stations',
    style: 'cloud_station',
    accentColor: '#ffb700',
    secondaryColor: '#00e5ff',
    heroImage: '/art/colossal_orbital_ring.jpg',
    prompt: 'Painterly floating sky station with glowing golden rounded observation domes, serene turquoise cloud layers, giant gas giant moon in twilight, Syd Mead concept art.',
    caption: 'JOVIAN REFINERY // GOLDEN OBSERVATION DOMES // CLOUD SEA',
    svgDataUri: generateProceduralSvg({ pageNumber: 151, style: 'cloud_station', title: 'Jovian Cloud Aerostat' }),
  },
  {
    id: 'station_perseus_theta_metropolis',
    title: 'Perseus-Theta High Terminal Metropolis',
    category: 'Megastructures & Stations',
    style: 'perseus_terminal',
    accentColor: '#00e676',
    secondaryColor: '#ffab00',
    prompt: 'Massive rotating orbital habitat city, illuminated window rings, automated cargo shuttles with engine burns, corporate spires, volumetric atmospheric dust, 8k.',
    caption: 'PERSEUS-THETA METROPOLIS // 12km HABITAT RING // COMBINE SECTOR',
    svgDataUri: generateProceduralSvg({ pageNumber: 901, style: 'perseus_terminal', title: 'Perseus-Theta Metropolis' }),
  },
  {
    id: 'station_high_slip_quarantine',
    title: 'High Mooring Slip 14 Quarantine',
    category: 'Megastructures & Stations',
    style: 'deep_quarantine',
    accentColor: '#ff1744',
    secondaryColor: '#ffd600',
    prompt: 'High mooring slip 14 quarantine station, ominous rotating red warning beacons, sealed decontamination clamps holding scarred bulk hauler, isolated deep space post.',
    caption: 'SLIP 14 QUARANTINE // CONTAMINATED HULL INDEX // LEVEL-4 ISOLATION',
    svgDataUri: generateProceduralSvg({ pageNumber: 916, style: 'deep_quarantine', title: 'High Mooring Slip 14' }),
  },
  {
    id: 'station_orbital_drydock_gantry',
    title: 'Orbital Construction Scaffolding',
    category: 'Megastructures & Stations',
    style: 'orbital_gantry',
    accentColor: '#ff9100',
    secondaryColor: '#00f3ff',
    heroImage: '/art/colossal_orbital_ring.jpg',
    prompt: 'Kilometer-long skeletal orbital drydock gantry, magnetic crane arms, brilliant blue electric welding flares against dark vacuum, modular starship under construction, 8k.',
    caption: 'SKELETAL ORBITAL DRYDOCK // MAGNETIC TETHERS // BLUE ARC FLUX',
    svgDataUri: generateProceduralSvg({ pageNumber: 15, style: 'orbital_gantry', title: 'Orbital Drydock Scaffolding' }),
  },
  {
    id: 'station_asteroid_ore_refinery',
    title: 'Asteroid Ore Smelting Spire',
    category: 'Megastructures & Stations',
    style: 'busy_stations',
    accentColor: '#ff6d00',
    secondaryColor: '#263238',
    prompt: 'Industrial asteroid smelting plant, glowing molten lithium rivers in vacuum containment, slag exhaust plumes, massive cargo transport moored, 8k.',
    caption: 'LITHIUM SMELTER // SLAG EXHAUST FLUSH // METALLIC DUST: HIGH',
    svgDataUri: generateProceduralSvg({ pageNumber: 45, style: 'busy_stations', title: 'Asteroid Ore Refinery' }),
  },
  {
    id: 'station_automated_transit_rail',
    title: 'Automated Mag-Rail Transit Tube',
    category: 'Megastructures & Stations',
    style: 'busy_stations',
    accentColor: '#00f3ff',
    secondaryColor: '#ffb700',
    prompt: 'High-speed magnetic vacuum transit tube linking orbital habitat spokes, glowing blue induction coils, automated shuttles streaking with light trails, 8k.',
    caption: 'MAG-RAIL SECTOR B // IN-TRANSIT VELOCITY 4,200 km/h // TRANSIT PARITY',
    svgDataUri: generateProceduralSvg({ pageNumber: 70, style: 'busy_stations', title: 'Automated Mag-Rail Tube' }),
  },
  {
    id: 'station_cargo_drone_swarm',
    title: 'Perseus Drone Swarm Berths',
    category: 'Megastructures & Stations',
    style: 'busy_stations',
    accentColor: '#ffb700',
    secondaryColor: '#00e5ff',
    prompt: 'Thousands of automated cargo drones buzzing like fireflies around colossal orbital freight spires, guidance laser grids crisscrossing dark space, 8k.',
    caption: 'DRONE SWARM DISPATCH // 8,400 ACTIVE UNITS // TRAFFIC PRIORITY: COMBINE',
    svgDataUri: generateProceduralSvg({ pageNumber: 85, style: 'busy_stations', title: 'Perseus Drone Swarm' }),
  },
  {
    id: 'station_combine_executive_spire',
    title: 'Corporate Executive Citadel Spire',
    category: 'Megastructures & Stations',
    style: 'perseus_terminal',
    accentColor: '#d50000',
    secondaryColor: '#cfd8dc',
    prompt: 'Looming monolithic corporate tower in high orbit, dark mirror glass facade reflecting stars, golden executive terrace suites, brutalist power, 8k.',
    caption: 'THORNE-VANCE CITADEL // BOARDROOM LEVEL 180 // SEALED TRANSIT',
    svgDataUri: generateProceduralSvg({ pageNumber: 935, style: 'perseus_terminal', title: 'Corporate Executive Spire' }),
  },

  // =========================================================
  // 4. HARD-SURFACE FLEETS & SHIPS (8 Representations)
  // =========================================================
  {
    id: 'fleet_solar_armada_convoy',
    title: 'Solar Sphere Armada Convoy (Hero 3)',
    category: 'Fleets & Ships',
    style: 'solar_armada',
    accentColor: '#ffd600',
    secondaryColor: '#ff6d00',
    heroImage: '/art/keel_ussc_geryon.jpg',
    prompt: 'Retro-futuristic hard sci-fi cargo fleet in deep space against a massive glowing yellow solar sphere, hard-surface titanium hulls, glowing yellow solar sensor arrays, Syd Mead.',
    caption: 'SOLAR SPHERE CONVOY // SYD MEAD HARD-SURFACE // PERSEUS FLEET',
    svgDataUri: generateProceduralSvg({ pageNumber: 201, style: 'solar_armada', title: 'Solar Sphere Armada' }),
  },
  {
    id: 'ship_ussc_geryon_full_keel',
    title: 'USSC Geryon — 4.12km Titanium Keel',
    category: 'Fleets & Ships',
    style: 'solitary_hauler',
    accentColor: '#00f3ff',
    secondaryColor: '#455a64',
    heroImage: '/art/keel_ussc_geryon.jpg',
    prompt: 'Colossal heavy bulk carrier USSC Geryon, 4.12 kilometer titanium keel, modular cargo containers, lateral cryostat tanks, solitary in pitch dark vacuum, 8k.',
    caption: 'USSC GERYON // HULL 0x992B-THETA // DISPLACEMENT 820,000t // INERTIAL CRUISE',
    svgDataUri: generateProceduralSvg({ pageNumber: 226, style: 'solitary_hauler', title: 'USSC Geryon Full Keel' }),
  },
  {
    id: 'ship_geryon_bow_observation',
    title: 'Geryon Bow Command Module',
    category: 'Fleets & Ships',
    style: 'solitary_hauler',
    accentColor: '#ffb700',
    secondaryColor: '#00f3ff',
    prompt: 'Forward command module and reinforced observation deck of the USSC Geryon, gold reflective armored viewports, communications sensor arrays, 8k.',
    caption: 'BOW COMMAND MODULE // BRIDGE TELEMETRY // VIEWPORT SHIELDED',
    svgDataUri: generateProceduralSvg({ pageNumber: 240, style: 'solitary_hauler', title: 'Geryon Bow Command' }),
  },
  {
    id: 'ship_dual_ion_exhaust_plume',
    title: 'Dual Ion Engine Burn Vector',
    category: 'Fleets & Ships',
    style: 'solitary_hauler',
    accentColor: '#00f3ff',
    secondaryColor: '#2979ff',
    prompt: 'Rear view of massive starship ion drive nozzles firing brilliant cyan exhaust plumes into deep space, particle wake stretching for kilometers, 8k.',
    caption: 'ION THRUSTERS 1 & 2 // EXHAUST VELOCITY 350 km/s // COIL PARITY: OK',
    svgDataUri: generateProceduralSvg({ pageNumber: 245, style: 'solitary_hauler', title: 'Dual Ion Engine Burn' }),
  },
  {
    id: 'ship_void_derelict_graveyard',
    title: 'The Lost Hull Concordance Graveyard',
    category: 'Fleets & Ships',
    style: 'derelict_graveyard',
    accentColor: '#78909c',
    secondaryColor: '#ff1744',
    prompt: 'Ghost fleet of broken starships floating in the Great Null Rift, twisted titanium beams, frozen vapor clouds, silent hull numbers fading in vacuum, 8k.',
    caption: 'LOST HULL GRAVEYARD // 412 OMITTED SHIPS // COMPILER GARBAGE VAULT',
    svgDataUri: generateProceduralSvg({ pageNumber: 981, style: 'derelict_graveyard', title: 'Lost Hull Graveyard' }),
  },
  {
    id: 'ship_stealth_inquest_cutter',
    title: 'Thorne-Vance Interdiction Cutter',
    category: 'Fleets & Ships',
    style: 'corporate_cutter',
    accentColor: '#d500f9',
    secondaryColor: '#212121',
    prompt: 'Predatory corporate enforcement vessel, angular stealth geometry, sweeping sensor dishes, cold blue cockpit glow, predatory presence, 8k.',
    caption: 'INQUEST CUTTER "ARBITER" // BLACK BOX INTERCEPT // WEAPONS DORMANT',
    svgDataUri: generateProceduralSvg({ pageNumber: 350, style: 'corporate_cutter', title: 'Inquest Interdiction Cutter' }),
  },
  {
    id: 'ship_cryo_radiator_fins',
    title: 'Spine Cryogenic Radiator Arrays',
    category: 'Fleets & Ships',
    style: 'cryogenic_hull',
    accentColor: '#00e5ff',
    secondaryColor: '#cfd8dc',
    prompt: 'Gigantic black heat radiator fins extending into vacuum along starship hull, glowing faintly with dissipated thermal energy, frost lines, 8k.',
    caption: 'CRYO RADIATOR FINS // THERMAL DISSIPATION: 4.8 MW // LIQUID HELIUM RECYCLE',
    svgDataUri: generateProceduralSvg({ pageNumber: 620, style: 'cryogenic_hull', title: 'Cryogenic Radiator Arrays' }),
  },
  {
    id: 'ship_salvage_tether_dock',
    title: 'Automated Salvage Tug Tether',
    category: 'Fleets & Ships',
    style: 'solitary_hauler',
    accentColor: '#ff9100',
    secondaryColor: '#00f3ff',
    prompt: 'Industrial salvage tug locking magnetic tractor cable onto the blackened, scarred hull of the USSC Geryon, towing into dock, 8k.',
    caption: 'SALVAGE TETHER #14-B // MAGNETIC TRACTOR GRIP // DEAD TOW PROTOCOL',
    svgDataUri: generateProceduralSvg({ pageNumber: 905, style: 'solitary_hauler', title: 'Automated Salvage Tug' }),
  },

  // =========================================================
  // 5. GRIMY INTERIORS & CRYO WELLS (8 Representations)
  // =========================================================
  {
    id: 'interior_phosphor_reactor_spine',
    title: 'Phosphor Engineering Spine Section 4 (Hero 4)',
    category: 'Interiors & Wells',
    style: 'phosphor_spine',
    accentColor: '#00e676',
    secondaryColor: '#1de9b6',
    heroImage: '/art/quantum_core_explosion.jpg',
    prompt: 'Grimy industrial sci-fi engineering well, toxic phosphor-green volumetric lighting, dripping liquid helium pipes, heavy metal deck grating, Alien/Dead Space aesthetic.',
    caption: 'SPINE SECTION 4 // PHOSPHOR ILLUMINATION // REACTION VAPORS 40Hz',
    svgDataUri: generateProceduralSvg({ pageNumber: 33, style: 'phosphor_spine', title: 'Phosphor Engineering Spine' }),
  },
  {
    id: 'interior_cryostat_choke_manifold',
    title: '1.8K Liquid Helium Choke Manifold',
    category: 'Interiors & Wells',
    style: 'cryostat_choke',
    accentColor: '#00b0ff',
    secondaryColor: '#ffffff',
    heroImage: '/art/chapter2_burnt_copper.jpg',
    prompt: 'Superconducting cryostat choke manifold at 1.8 Kelvin, thick white rime frost on stainless steel conduits, bubbling cryo gauges, icy cyan spotlights, 8k.',
    caption: 'CRYOSTAT CHOKE 1.8K // VACUUM JACKETED HE-4 // PRESSURE: 4 ATM',
    svgDataUri: generateProceduralSvg({ pageNumber: 49, style: 'cryostat_choke', title: 'Liquid Helium Choke' }),
  },
  {
    id: 'interior_stasis_crypt_vault',
    title: 'Crew Hibernation Stasis Crypt',
    category: 'Interiors & Wells',
    style: 'stasis_vaults',
    accentColor: '#2979ff',
    secondaryColor: '#00e5ff',
    heroImage: '/art/cryo_stasis_well_chamber.jpg',
    prompt: 'Endless rows of frosted stasis hibernation capsules in starship vault, faint cyan vitals displays, rhythmic respiratory tubes, condensation fog, 8k.',
    caption: 'STASIS CRYPT 02 // 48 CREW SEDATED // NEURO-LATTICE DORMANT',
    svgDataUri: generateProceduralSvg({ pageNumber: 426, style: 'stasis_vaults', title: 'Stasis Hibernation Crypt' }),
  },
  {
    id: 'interior_depressurized_cargo_bay4',
    title: 'Cavernous Depressurized Bay 4',
    category: 'Interiors & Wells',
    style: 'cargo_void',
    accentColor: '#ffab00',
    secondaryColor: '#263238',
    prompt: 'Colossal unpressurized cargo hold, kilometers of titanium ribbing disappearing into darkness, magnetic hold-down clamps, floating dust particles, 8k.',
    caption: 'CARGO BAY 4 // UNRENDERED ANOMALY // 80,000t BULK ORE DISCREPANCY',
    svgDataUri: generateProceduralSvg({ pageNumber: 160, style: 'cargo_void', title: 'Depressurized Cargo Bay 4' }),
  },
  {
    id: 'interior_mark9_hologram_core',
    title: 'Mark IX Decoupler Core Chamber',
    category: 'Interiors & Wells',
    style: 'quantum_schematic',
    accentColor: '#00f3ff',
    secondaryColor: '#76ff03',
    heroImage: '/art/chapter9_core_containment.jpg',
    prompt: 'Experimental Mark IX Resonant Decoupler Core suspended in massive magnetic cradle, glowing blue plasma flux, cryo steam venting, 8k.',
    caption: 'DECOUPLER CORE CHAMBER // CRADLE LEVITATION: 100% // FLUX 40.0Hz',
    svgDataUri: generateProceduralSvg({ pageNumber: 205, style: 'quantum_schematic', title: 'Mark IX Core Chamber' }),
  },
  {
    id: 'interior_uncoupled_catwalk_spine',
    title: 'Zero-G Uncoupled Spine Catwalk',
    category: 'Interiors & Wells',
    style: 'cryogenic_hull',
    accentColor: '#00f3ff',
    secondaryColor: '#ffb700',
    prompt: 'Maintenance engineer in magnetic boots walking zero-gravity catwalk along interior starship spine, floating frozen droplets of coolant, dark industrial expanse, 8k.',
    caption: 'SPINE CATWALK ZERO-G // RESTRAINT TETHER SECURE // WARD, M. ON LOG',
    svgDataUri: generateProceduralSvg({ pageNumber: 230, style: 'cryogenic_hull', title: 'Zero-G Spine Catwalk' }),
  },
  {
    id: 'interior_emergency_scram_valve',
    title: 'Manual Drive Scram Valve Lever',
    category: 'Interiors & Wells',
    style: 'phosphor_spine',
    accentColor: '#ff1744',
    secondaryColor: '#ffd600',
    prompt: 'Heavy mechanical emergency scram lever clamped with tamper-evident yellow seal, hydraulic pressure gauges pegged in red, emergency strobe, 8k.',
    caption: 'MANUAL SCRAM SYSTEM // TAMPER SEAL INTACT // DO NOT OVERRIDE',
    svgDataUri: generateProceduralSvg({ pageNumber: 215, style: 'phosphor_spine', title: 'Emergency Scram Valve' }),
  },
  {
    id: 'interior_titanium_stress_cracks',
    title: 'Acoustic Fatigue Stress Fractures',
    category: 'Interiors & Wells',
    style: 'phosphor_spine',
    accentColor: '#ff3d00',
    secondaryColor: '#00f3ff',
    prompt: 'Close-up of hairline spiderweb stress fractures creeping across interior titanium bulkhead wall, acoustic sensor taped to metal, 8k.',
    caption: 'BULKHEAD CRACK TV-882-B // ACOUSTIC RESONANCE FATIGUE // REPORT SEALED',
    svgDataUri: generateProceduralSvg({ pageNumber: 2, style: 'phosphor_spine', title: 'Acoustic Stress Fractures' }),
  },

  // =========================================================
  // 6. ARCHITECTURE, TRANSIT & TRIBUNALS (6 Representations)
  // =========================================================
  {
    id: 'arch_transit_corridor_shaft',
    title: 'Architectural Transit Spine (Hero 5)',
    category: 'Architecture & Transit',
    style: 'transit_corridor',
    accentColor: '#ff5722',
    secondaryColor: '#cfd8dc',
    heroImage: '/art/perseus_terminal_citadel.jpg',
    prompt: 'Architectural sci-fi corridor concept sketch, sharp perspective, overhead volumetric sunlight, industrial signage stencils, ghosted double-vision lines.',
    caption: 'TRANSIT SPINE // VOLUMETRIC LIGHT SHAFT // COMBINE ARMORED TRUSS',
    svgDataUri: generateProceduralSvg({ pageNumber: 676, style: 'transit_corridor', title: 'Architectural Transit Spine' }),
  },
  {
    id: 'arch_corporate_inquest_tribunal',
    title: 'Thorne-Vance Tribunal Chamber',
    category: 'Architecture & Transit',
    style: 'corporate_tribunal',
    accentColor: '#d50000',
    secondaryColor: '#212121',
    prompt: 'Brutalist corporate tribunal chamber, polished black obsidian conference table, cold sterile surgical downlights, holographic legal transcript projection, 8k.',
    caption: 'EXECUTIVE TRIBUNAL // SEVENTY-HOUR DEBRIEF // SEALED RECORDING',
    svgDataUri: generateProceduralSvg({ pageNumber: 920, style: 'corporate_tribunal', title: 'Thorne-Vance Tribunal' }),
  },
  {
    id: 'arch_convalescent_recovery_wing',
    title: 'Convalescent Observation Bay',
    category: 'Architecture & Transit',
    style: 'chrono_psych_ward',
    accentColor: '#00b0ff',
    secondaryColor: '#e040fb',
    prompt: 'Clinical quarantine medical ward, reinforced observation window overlooking distant star field, diagnostic retinal monitors, solitary figure in hospital gown, 8k.',
    caption: 'CONVALESCENT WING 7 // PERSPECTIVE HORIZON // MARCUS WARD QUARANTINE',
    svgDataUri: generateProceduralSvg({ pageNumber: 940, style: 'chrono_psych_ward', title: 'Convalescent Observation Bay' }),
  },
  {
    id: 'arch_decontamination_airlock',
    title: 'Decontamination Cycle Airlock',
    category: 'Architecture & Transit',
    style: 'transit_corridor',
    accentColor: '#ffd600',
    secondaryColor: '#00e5ff',
    prompt: 'Massive circular airlock door sealing with pressurized hiss, chemical decontamination fog spraying from ceiling nozzles, amber warning strobes, 8k.',
    caption: 'AIRLOCK DECON CYCLE // PRESSURE EQUALIZATION // SEAL PARITY: 100%',
    svgDataUri: generateProceduralSvg({ pageNumber: 10, style: 'transit_corridor', title: 'Decontamination Airlock' }),
  },
  {
    id: 'arch_mess_deck_chronozepine',
    title: 'Mess Deck Scratched Table',
    category: 'Architecture & Transit',
    style: 'transit_corridor',
    accentColor: '#ffab00',
    secondaryColor: '#37474f',
    prompt: 'Grimy starship mess hall table, scratched composite surface, half-empty mug of synth-coffee next to a glass vial of Chronozepine sliding across table, 8k.',
    caption: 'MESS TABLE 4 // GLASS VIAL CHRONOZEPINE // SHIFT DEBRIEF COMPLETE',
    svgDataUri: generateProceduralSvg({ pageNumber: 35, style: 'transit_corridor', title: 'Mess Deck Table' }),
  },
  {
    id: 'arch_bridge_navigation_helm',
    title: 'Navigation Bridge Holo-Helm',
    category: 'Architecture & Transit',
    style: 'corporate_tribunal',
    accentColor: '#00f3ff',
    secondaryColor: '#e040fb',
    prompt: 'Geryon flight bridge, multi-tiered holographic navigation sphere showing unplotted Null Rift jump coordinates, Elena Cruz at helm console, 8k.',
    caption: 'HOLO-HELM CONSOLE // COORDINATE LEDGER COMPILED // JUMP VECTOR ARMED',
    svgDataUri: generateProceduralSvg({ pageNumber: 110, style: 'corporate_tribunal', title: 'Bridge Navigation Helm' }),
  },

  // =========================================================
  // 7. EXISTENTIAL HORROR & COSMIC LEDGER (6 Representations)
  // =========================================================
  {
    id: 'horror_synaptic_chronognostic_tear',
    title: 'Chronognostic Synaptic Tearing',
    category: 'Existential Horror & Ledger',
    style: 'synaptic_tear',
    accentColor: '#ff1744',
    secondaryColor: '#00f3ff',
    prompt: 'Haunting psychological dark sci-fi illustration of human neural pathways experiencing chronognostic aphasia, glowing synaptic lattice with violet and crimson micro-fractures, 8k.',
    caption: 'NEURO-LATTICE TEARING // RETINAL BLEED 40Hz // SYNAPSE DISCORDANCE',
    svgDataUri: generateProceduralSvg({ pageNumber: 451, style: 'synaptic_tear', title: 'Synaptic Chronognostic Tearing' }),
  },
  {
    id: 'horror_vitrified_obsidian_ledger',
    title: 'Vitrified Obsidian Cosmic Ledger Tablet',
    category: 'Existential Horror & Ledger',
    style: 'cosmic_ledger',
    accentColor: '#ffd600',
    secondaryColor: '#ff3d00',
    heroImage: '/art/chapter21_cosmic_ledger.jpg',
    prompt: 'Alien cosmic ledger data matrix carved into vitrified black glass, glowing amber coordinates and mass reconciliation integrals, ominous mathematical architecture, 8k.',
    caption: 'COSMIC LEDGER TABLET // MASS PARITY BALANCED // 1,000 PAGES RECONCILED',
    svgDataUri: generateProceduralSvg({ pageNumber: 501, style: 'cosmic_ledger', title: 'Vitrified Ledger Tablet' }),
  },
  {
    id: 'horror_spectators_in_the_gutter',
    title: 'The Observers in the Gutter',
    category: 'Existential Horror & Ledger',
    style: 'spectators_margin',
    accentColor: '#aa00ff',
    secondaryColor: '#ff1744',
    heroImage: '/art/chapter13_observers_gutter.jpg',
    prompt: 'Eerie cosmic horror depiction of vast, patient geometric silhouettes lurking inside the timeless stillness between Planck moments, non-Euclidean angles watching crew, 8k.',
    caption: 'OBSERVERS IN THE MARGIN // TIMELESS PATIENCE // THE SEAM WATCHES',
    svgDataUri: generateProceduralSvg({ pageNumber: 301, style: 'spectators_margin', title: 'Observers in the Gutter' }),
  },
  {
    id: 'horror_recrystallization_shear_shock',
    title: 'Re-Crystallization Shear Shock',
    category: 'Existential Horror & Ledger',
    style: 'recrystallization_shear',
    accentColor: '#ff3d00',
    secondaryColor: '#00f3ff',
    heroImage: '/art/chapter30_ignition_next_moment.jpg',
    prompt: 'Violent physical reality re-crystallization when ship snaps back into physical space, atoms locking violently, atmospheric shockwaves, titanium micro-fractures, 8k.',
    caption: 'MOMENT RE-IGNITION // RE-CRYSTALLIZATION SHEAR // VIOLENT PHYSICAL SNAP',
    svgDataUri: generateProceduralSvg({ pageNumber: 751, style: 'recrystallization_shear', title: 'Re-Crystallization Shear Shock' }),
  },
  {
    id: 'horror_missing_mass_audit_anomaly',
    title: 'The 0.042kg Missing Mass Audit Anomaly',
    category: 'Existential Horror & Ledger',
    style: 'cosmic_ledger',
    accentColor: '#ff9100',
    secondaryColor: '#ff1744',
    heroImage: '/art/obsidian_ledger_void.jpg',
    prompt: 'Cosmic compiler terminal displaying 0.042 kilogram unallocated human mass error, missing matter flagged for purge by automated cosmic garbage collection, 8k.',
    caption: 'MASS AUDIT DEFICIT // 0.042 kg ANOMALY // GARBAGE COLLECTION QUEUED',
    svgDataUri: generateProceduralSvg({ pageNumber: 526, style: 'cosmic_ledger', title: 'Missing Mass Audit Anomaly' }),
  },
  {
    id: 'horror_language_dissolution_syntax',
    title: 'Recursive Language Syntax Dissolution',
    category: 'Existential Horror & Ledger',
    style: 'synaptic_tear',
    accentColor: '#e040fb',
    secondaryColor: '#00e5ff',
    heroImage: '/art/chapter17_memories_tomorrow.jpg',
    prompt: 'Abstract dark sci-fi representation of human language breaking down into recursive mathematical symbols, floating decaying sentences, shattered grammar, 8k.',
    caption: 'SYNTAX DISSOLUTION // VERBAL LATENCY: 3.2s // THOUGHT RECURSION LOOP',
    svgDataUri: generateProceduralSvg({ pageNumber: 430, style: 'synaptic_tear', title: 'Language Syntax Dissolution' }),
  },
];

export const STYLE_PAINTINGS: Record<string, string> = {
  quantum_schematic: '/art/chapter9_core_containment.jpg',
  particulate_decay: '/art/synaptic_decay_chamber.jpg',
  planck_shear: '/art/chapter1_tick_and_void.jpg',
  temporal_freeze: '/art/chapter11_stillness_breathes.jpg',
  chromatic_break: '/art/chapter30_ignition_next_moment.jpg',
  cosmic_flare: '/art/planck_ignition_event.jpg',
  event_horizon: '/art/chapter5_null_rift.jpg',
  stellar_nursery: '/art/non_euclidean_void_chasm.jpg',
  unrendered_void: '/art/chapter11_stillness_breathes.jpg',
  vast_spaces: '/art/keel_ussc_geryon.jpg',
  deep_quarantine: '/art/tribunal_inquisition_hall.jpg',
  orbital_gantry: '/art/colossal_orbital_ring.jpg',
  busy_stations: '/art/asteroid_smelting_forge.jpg',
  perseus_terminal: '/art/perseus_terminal_citadel.jpg',
  jovian_cloud: '/art/colossal_orbital_ring.jpg',
  solitary_hauler: '/art/derelict_ghost_freighter.jpg',
  industrial_keel: '/art/keel_ussc_geryon.jpg',
  solar_fleet: '/art/keel_ussc_geryon.jpg',
  combat_corvette: '/art/caesura_jump_gate.jpg',
  cryogenic_hull: '/art/chapter2_burnt_copper.jpg',
  phosphor_spine: '/art/chapter2_burnt_copper.jpg',
  cryostat_choke: '/art/cryo_stasis_well_chamber.jpg',
  stasis_vaults: '/art/stasis_pod_awakening.jpg',
  cargo_void: '/art/derelict_ghost_freighter.jpg',
  transit_tube: '/art/perseus_terminal_citadel.jpg',
  tribunal_hall: '/art/tribunal_inquisition_hall.jpg',
  hydroponics: '/art/cryo_stasis_well_chamber.jpg',
  quarantine_brig: '/art/tribunal_inquisition_hall.jpg',
  synaptic_tear: '/art/chapter17_memories_tomorrow.jpg',
  obsidian_ledger: '/art/chapter21_cosmic_ledger.jpg',
  margin_entity: '/art/chapter13_observers_gutter.jpg',
  cosmic_ledger: '/art/chapter21_cosmic_ledger.jpg',
  spectators_margin: '/art/chapter13_observers_gutter.jpg',
  recrystallization_shear: '/art/chapter30_ignition_next_moment.jpg',
};

export const CHARACTER_PORTRAITS: Record<CharacterId, string> = {
  marcus_ward: '/art/marcus_ward_portrait.jpg',
  elena_cruz: '/art/elena_cruz_portrait.jpg',
  dr_simon_peter: '/art/dr_simon_peter_portrait.jpg',
  cadet_silas: '/art/silas_vance_portrait.jpg',
  inquisitor_thorne: '/art/inquisitor_thorne_portrait.jpg',
  margin_spectator: '/art/spectator_margin_entity.jpg',
};

function resolveHeroImage(entry: VisualEntry): VisualEntry {
  if (entry.heroImage) return entry;
  if (entry.characterId && CHARACTER_PORTRAITS[entry.characterId]) {
    return { ...entry, heroImage: CHARACTER_PORTRAITS[entry.characterId] };
  }
  const fallback = STYLE_PAINTINGS[entry.style] || '/art/planck_ignition_event.jpg';
  return { ...entry, heroImage: fallback };
}

export function getAllVisualRepresentations(): VisualEntry[] {
  return [...VISUAL_REPRESENTATIONS.map(resolveHeroImage), ...GENERATIVE_VISUAL_ENTRIES];
}

export function getVisualEntryById(id: string): VisualEntry | undefined {
  const entry =
    VISUAL_REPRESENTATIONS.find((v) => v.id === id) ||
    GENERATIVE_VISUAL_ENTRIES.find((v) => v.id === id);
  return entry ? resolveHeroImage(entry) : undefined;
}

export function getVisualsByCategory(category: VisualEntry['category']): VisualEntry[] {
  if (category === 'Generative Concept Art (150)') {
    return GENERATIVE_VISUAL_ENTRIES;
  }
  return VISUAL_REPRESENTATIONS.filter((v) => v.category === category).map(resolveHeroImage);
}

/**
 * Maps any page number (1-452+) to its primary visual representation
 * Always resolves to a high-resolution photorealistic concept art painting
 * matching the exact narrative beat and character focus of each Act.
 */
export function getVisualForPage(pageNum: number): VisualEntry {
  let base: VisualEntry;

  // Front Matter (1-7): Dedication & Pre-Flight
  if (pageNum <= 4) base = VISUAL_REPRESENTATIONS[0]; // Marcus Neutral Pre-Flight
  else if (pageNum <= 7) base = VISUAL_REPRESENTATIONS[12] || VISUAL_REPRESENTATIONS[0]; // Perseus Jump Gate

  // Act 1 (8-45): The Planck Threshold
  else if (pageNum >= 8 && pageNum <= 15) base = VISUAL_REPRESENTATIONS[12] || VISUAL_REPRESENTATIONS[0]; // Chapter 1 Jump Gate
  else if (pageNum >= 16 && pageNum <= 25) base = VISUAL_REPRESENTATIONS[4]; // Elena Cruz Chief Architect
  else if (pageNum >= 26 && pageNum <= 35) base = VISUAL_REPRESENTATIONS[33] || VISUAL_REPRESENTATIONS[8]; // Silas Vance / Chapter 2 Burnt Copper
  else if (pageNum <= 45) base = VISUAL_REPRESENTATIONS[22] || VISUAL_REPRESENTATIONS[12]; // Industrial Keel USSC Geryon

  // Act 2 (46-93): Chronognostic Dissociation & Dr. Peter
  else if (pageNum <= 55) base = VISUAL_REPRESENTATIONS[1]; // Marcus Weary
  else if (pageNum <= 68) base = VISUAL_REPRESENTATIONS[7] || VISUAL_REPRESENTATIONS[0]; // Dr. Simon Peter
  else if (pageNum <= 80) base = VISUAL_REPRESENTATIONS[34] || VISUAL_REPRESENTATIONS[1]; // Cryo Stasis Well
  else if (pageNum <= 93) base = VISUAL_REPRESENTATIONS[43] || VISUAL_REPRESENTATIONS[2]; // Synaptic Decay Chamber

  // Act 3 (94-151): The Unrendered Margin & Great Null Rift
  else if (pageNum <= 115) base = VISUAL_REPRESENTATIONS[13] || VISUAL_REPRESENTATIONS[12]; // Chapter 5 Null Rift
  else if (pageNum <= 135) base = VISUAL_REPRESENTATIONS[36] || VISUAL_REPRESENTATIONS[12]; // Chapter 9 Core Containment Rupture
  else if (pageNum <= 151) base = VISUAL_REPRESENTATIONS[14] || VISUAL_REPRESENTATIONS[13]; // Non-Euclidean Void Chasm

  // Act 4 (152-205): The Obsidian Ledger
  else if (pageNum <= 170) base = VISUAL_REPRESENTATIONS[45] || VISUAL_REPRESENTATIONS[14]; // Chapter 13 Observers in Gutter
  else if (pageNum <= 188) base = VISUAL_REPRESENTATIONS[48] || VISUAL_REPRESENTATIONS[0]; // Chapter 17 Memories Tomorrow
  else if (pageNum <= 205) base = VISUAL_REPRESENTATIONS[44] || VISUAL_REPRESENTATIONS[14]; // Chapter 21 Cosmic Ledger Core

  // Act 5 (206-247): Cognitive Decay & Inquest
  else if (pageNum <= 218) base = VISUAL_REPRESENTATIONS[2]; // Marcus Synaptic Bleed
  else if (pageNum <= 232) base = VISUAL_REPRESENTATIONS[9] || VISUAL_REPRESENTATIONS[1]; // Inquisitor Thorne
  else if (pageNum <= 247) base = VISUAL_REPRESENTATIONS[41] || VISUAL_REPRESENTATIONS[2]; // Tribunal Inquisition Hall

  // Act 6 (248-293): The Stillness That Breathes & The Margin Spectator
  else if (pageNum <= 270) base = VISUAL_REPRESENTATIONS[15] || VISUAL_REPRESENTATIONS[14]; // Chapter 11 Stillness That Breathes
  else if (pageNum <= 293) base = VISUAL_REPRESENTATIONS[11]; // The Margin Spectator

  // Act 7 (294-333): Stripping the Keel
  else if (pageNum <= 312) base = VISUAL_REPRESENTATIONS[28] || VISUAL_REPRESENTATIONS[22]; // Derelict Ghost Freighter / Jettison
  else if (pageNum <= 333) base = VISUAL_REPRESENTATIONS[25] || VISUAL_REPRESENTATIONS[12]; // Colossal Orbital Ring / Drydock

  // Act 8 (334-369): The Final Computation
  else if (pageNum <= 350) base = VISUAL_REPRESENTATIONS[36] || VISUAL_REPRESENTATIONS[16]; // Quantum Core Rupture
  else if (pageNum <= 369) base = VISUAL_REPRESENTATIONS[26] || VISUAL_REPRESENTATIONS[4]; // Asteroid Smelting Forge / Thermal Core

  // Act 9 (370-401): Breaking the Seam & Climax
  else if (pageNum <= 385) base = VISUAL_REPRESENTATIONS[46] || VISUAL_REPRESENTATIONS[12]; // Chapter 30 Ignition of Next Moment
  else if (pageNum <= 401) base = VISUAL_REPRESENTATIONS[12]; // Caesura Gate Breakthrough

  // Act 10 (402-428): The Next Clock-Tick & Perseus Citadel
  else if (pageNum <= 415) base = VISUAL_REPRESENTATIONS[27] || VISUAL_REPRESENTATIONS[12]; // Perseus Terminal Citadel
  else if (pageNum <= 428) base = VISUAL_REPRESENTATIONS[35] || VISUAL_REPRESENTATIONS[1]; // Stasis Pod Awakening

  // Appendices & Advance Hook for Book 2 (429-452)
  else if (pageNum <= 445) base = VISUAL_REPRESENTATIONS[24] || VISUAL_REPRESENTATIONS[27]; // High Quarantine Citadel slip
  else if (pageNum === 452) base = VISUAL_REPRESENTATIONS[11]; // Margin Spectator Book Two Cliffhanger
  else {
    base = VISUAL_REPRESENTATIONS[46] || VISUAL_REPRESENTATIONS[12]; // Final Planck Ignition
  }

  return resolveHeroImage(base);
}
